import React from 'react';
import Tilt from 'react-parallax-tilt';
import { motion } from 'framer-motion';
import { Users, Target, Trophy, TrendingUp, Calendar } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import CountUp from '../common/CountUp';
import AmbientBackground from '../common/AmbientBackground';

// Liquid glass bento stats - golf-appropriate palette only
const BentoStats = () => {
  const { isDarkMode } = useTheme();

  const tiltProps = {
    tiltMaxAngleX: 5,
    tiltMaxAngleY: 5,
    perspective: 1400,
    transitionSpeed: 1500,
    scale: 1.01,
    gyroscope: false,
    tiltEnable: typeof window !== 'undefined' && window.innerWidth >= 768,
    glareEnable: true,
    glareMaxOpacity: 0.15,
    glareColor: isDarkMode ? '#ffffff' : '#ffffff',
    glarePosition: 'top'
  };

  // Glass card classes
  const glassCard = isDarkMode
    ? 'bg-white/[0.04] border border-white/[0.08] backdrop-blur-xl'
    : 'bg-white/60 border border-white/80 backdrop-blur-xl shadow-lg shadow-emerald-900/5';

  // Top shine for liquid glass effect
  const shineStyle = {
    background: isDarkMode
      ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.95), transparent)'
  };

  const animateCard = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }
  });

  const SmallCard = ({ icon: Icon, iconColor, value, suffix, label, delay }) => (
    <motion.div {...animateCard(delay)}>
      <Tilt {...tiltProps} className="h-full">
        <div className={`relative h-full p-4 sm:p-5 rounded-2xl overflow-hidden ${glassCard}`}>
          <div className="absolute inset-x-0 top-0 h-px" style={shineStyle} />
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{
              background: isDarkMode
                ? 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.05), transparent 60%)'
                : 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.7), transparent 60%)'
            }}
          />
          <div className="relative">
            <div className={`inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl mb-3 ${iconColor}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className={`text-3xl sm:text-4xl font-bold mb-1 leading-none ${
              isDarkMode ? 'text-white' : 'text-slate-900'
            }`}>
              <CountUp end={value} duration={2000} suffix={suffix || ''} />
            </div>
            <div className={`text-xs sm:text-sm ${
              isDarkMode ? 'text-slate-400' : 'text-slate-600'
            }`}>
              {label}
            </div>
          </div>
        </div>
      </Tilt>
    </motion.div>
  );

  return (
    <section className="py-16 sm:py-20 lg:py-28 relative overflow-hidden">
      <AmbientBackground variant="gold" />

      <div className="container-custom relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-14"
        >
          <div className="inline-flex items-center justify-center gap-2 mb-4">
            <div className={`h-px w-8 ${isDarkMode ? 'bg-amber-400/60' : 'bg-amber-500/60'}`} />
            <span className={`text-[10px] sm:text-xs font-semibold uppercase tracking-[0.3em] ${
              isDarkMode ? 'text-amber-400' : 'text-amber-600'
            }`}>
              The union
            </span>
            <div className={`h-px w-8 ${isDarkMode ? 'bg-amber-400/60' : 'bg-amber-500/60'}`} />
          </div>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold font-cursive mb-3 ${
            isDarkMode ? 'text-white' : 'text-slate-900'
          }`}>
            By the numbers
          </h2>
          <p className={`text-base sm:text-lg ${
            isDarkMode ? 'text-slate-400' : 'text-slate-600'
          }`}>
            A thriving golf community across the Free State
          </p>
        </motion.div>

        {/* Mobile layout */}
        <div className="lg:hidden space-y-3 sm:space-y-4 max-w-md mx-auto">
          {/* Feature card */}
          <motion.div {...animateCard(0)}>
            <div className="relative rounded-3xl overflow-hidden">
              {/* Glow behind */}
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-emerald-500/40 via-emerald-600/20 to-amber-500/20 blur-xl opacity-60" />

              <div className="relative rounded-3xl overflow-hidden p-7 border border-emerald-400/20"
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(5, 95, 70, 0.4) 0%, rgba(6, 78, 59, 0.5) 50%, rgba(4, 47, 46, 0.6) 100%)'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
                }}
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl" />

                <div className="relative text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-5 bg-white/15 backdrop-blur-md border border-white/20">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.25em] mb-2 text-emerald-100/90">
                    Active Community
                  </div>
                  <div className="text-6xl sm:text-7xl font-bold text-white mb-2 font-cursive leading-none tracking-tight">
                    <CountUp end={5500} duration={2500} suffix="+" />
                  </div>
                  <div className="text-base text-white/90 font-medium mb-4">
                    Registered golfers
                  </div>
                  <p className="text-xs text-white/70 max-w-xs mx-auto">
                    From weekend amateurs to competitive players, our community keeps growing every season.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <SmallCard
              icon={Target}
              iconColor="bg-gradient-to-br from-amber-400 to-amber-600"
              value={45}
              label="Golf clubs"
              delay={0.1}
            />
            <SmallCard
              icon={TrendingUp}
              iconColor="bg-gradient-to-br from-blue-400 to-blue-600"
              value={75}
              suffix="+"
              label="Years of excellence"
              delay={0.15}
            />
            <SmallCard
              icon={Trophy}
              iconColor="bg-gradient-to-br from-emerald-400 to-emerald-600"
              value={46}
              label="Club affiliates"
              delay={0.2}
            />
            <SmallCard
              icon={Calendar}
              iconColor="bg-gradient-to-br from-slate-500 to-slate-700"
              value={12}
              label="Major events yearly"
              delay={0.25}
            />
          </div>
        </div>

        {/* Desktop bento */}
        <div className="hidden lg:grid lg:grid-cols-4 lg:grid-rows-2 gap-5 auto-rows-fr">
          {/* Feature */}
          <motion.div {...animateCard(0)} className="lg:row-span-2 lg:col-span-2 relative">
            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-emerald-500/40 via-emerald-600/20 to-amber-500/20 blur-xl opacity-60" />
            <Tilt {...tiltProps} className="h-full relative">
              <div
                className="relative h-full min-h-[340px] p-10 rounded-3xl overflow-hidden border border-emerald-400/20"
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(5, 95, 70, 0.4) 0%, rgba(6, 78, 59, 0.5) 50%, rgba(4, 47, 46, 0.6) 100%)'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
                }}
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-amber-400/10 blur-2xl" />

                <div className="relative z-10 flex flex-col justify-between h-full">
                  <div>
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6 bg-white/15 backdrop-blur-md border border-white/20">
                      <Users className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-[0.25em] mb-3 text-emerald-100/90">
                      Active Community
                    </div>
                    <div className="text-7xl xl:text-[8rem] font-bold text-white mb-2 font-cursive leading-none tracking-tight">
                      <CountUp end={5500} duration={2500} suffix="+" />
                    </div>
                    <div className="text-xl text-white/90 font-medium">
                      Registered golfers
                    </div>
                  </div>
                  <p className="text-sm text-white/75 mt-6 max-w-sm leading-relaxed">
                    From weekend amateurs to competitive players, our community
                    keeps growing every season.
                  </p>
                </div>
              </div>
            </Tilt>
          </motion.div>

          <SmallCard
            icon={Target}
            iconColor="bg-gradient-to-br from-amber-400 to-amber-600"
            value={45}
            label="Golf clubs"
            delay={0.1}
          />
          <SmallCard
            icon={TrendingUp}
            iconColor="bg-gradient-to-br from-blue-400 to-blue-600"
            value={75}
            suffix="+"
            label="Years of excellence"
            delay={0.15}
          />
          <SmallCard
            icon={Trophy}
            iconColor="bg-gradient-to-br from-emerald-400 to-emerald-600"
            value={46}
            label="Club affiliates"
            delay={0.2}
          />
          <SmallCard
            icon={Calendar}
            iconColor="bg-gradient-to-br from-slate-500 to-slate-700"
            value={12}
            label="Major events yearly"
            delay={0.25}
          />
        </div>
      </div>
    </section>
  );
};

export default BentoStats;